// Create NEW file: /src/components/admin/BrandForm.jsx
// Form for creating/editing brands with multi-language support

import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';

const BrandForm = ({ brand, onSubmit, onCancel, loading }) => {
  const [activeLanguage, setActiveLanguage] = useState('ka');
  const [formData, setFormData] = useState({
    name_ka: '',
    name_en: '',
    name_ru: '',
    logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (brand) {
      setFormData({
        name_ka: brand.name_ka || brand.name || '',
        name_en: brand.name_en || '',
        name_ru: brand.name_ru || '',
        logo: null
      });
      if (brand.logo) {
        setLogoPreview(`https://kid-o.ge${brand.logo}`);
      }
    }
  }, [brand]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, logo: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name_ka.trim()) {
      alert('Georgian brand name is required!');
      setActiveLanguage('ka');
      return;
    }
    
    const data = new FormData();
    data.append('name_ka', formData.name_ka);
    data.append('name_en', formData.name_en);
    data.append('name_ru', formData.name_ru);
    if (formData.logo) {
      data.append('logo', formData.logo);
    }
    
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="form-container">
      {/* Language Tabs */}
      <div className="language-tabs">
        <button
          type="button"
          className={`language-tab ${activeLanguage === 'ka' ? 'active' : ''}`}
          onClick={() => setActiveLanguage('ka')}
        >
          🇬🇪 Georgian (ქართული) *
        </button>
        <button
          type="button"
          className={`language-tab ${activeLanguage === 'en' ? 'active' : ''}`}
          onClick={() => setActiveLanguage('en')}
        >
          🇬🇧 English
        </button>
        <button
          type="button"
          className={`language-tab ${activeLanguage === 'ru' ? 'active' : ''}`}
          onClick={() => setActiveLanguage('ru')}
        >
          🇷🇺 Russian (Русский)
        </button>
      </div>

      {/* Georgian Field */}
      {activeLanguage === 'ka' && (
        <div className="language-content">
          <div className="language-note">
            <strong>ქართული</strong> - ძირითადი ენა (სავალდებულო)
          </div>
          <div className="form-grid">
            <div className="form-group">
              <Input
                label="Brand Name (Georgian)"
                name="name_ka"
                value={formData.name_ka}
                onChange={handleChange}
                placeholder="მაგ: Apple, Samsung, Nike"
                required
              />
            </div>
          </div>
        </div>
      )}

      {/* English Field */}
      {activeLanguage === 'en' && (
        <div className="language-content">
          <div className="language-note">
            <strong>English</strong> - Optional translation
          </div>
          <div className="form-grid">
            <div className="form-group">
              <Input
                label="Brand Name (English)"
                name="name_en"
                value={formData.name_en}
                onChange={handleChange}
                placeholder="e.g., Apple, Samsung, Nike"
              />
            </div>
          </div>
        </div>
      )}

      {/* Russian Field */}
      {activeLanguage === 'ru' && (
        <div className="language-content">
          <div className="language-note">
            <strong>Русский</strong> - Необязательный перевод
          </div>
          <div className="form-grid">
            <div className="form-group">
              <Input
                label="Brand Name (Russian)"
                name="name_ru"
                value={formData.name_ru}
                onChange={handleChange}
                placeholder="например, Apple, Samsung, Nike"
              />
            </div>
          </div>
        </div>
      )}

      {/* Logo Upload */}
      <div className="common-fields">
        <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
          Brand Logo (Optional)
        </h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label className="form-label">Brand Logo</label>
            <div className="file-input-wrapper">
              <input
                type="file"
                id="brand-logo"
                className="file-input"
                accept="image/*"
                onChange={handleLogoChange}
              />
              <label htmlFor="brand-logo" className="file-input-label">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🖼️</div>
                  <div>Click to upload logo</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
                    PNG, JPG, WEBP (Max 5MB)
                  </div>
                </div>
              </label>
            </div>
            {logoPreview && (
              <div className="file-preview" style={{ marginTop: '1rem' }}>
                <img 
                  src={logoPreview} 
                  alt="Logo preview" 
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '200px',
                    objectFit: 'contain',
                    background: '#f5f5f5',
                    padding: '1rem',
                    borderRadius: '8px'
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="form-actions">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={loading}>
          {brand ? 'Update Brand' : 'Create Brand'}
        </Button>
      </div>
    </form>
  );
};

export default BrandForm;